import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import moment from "moment";
import { HttpService } from "../../service/HttpService";
import { useAuth } from "../../context/AuthProvider";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import "./Harvest.scss";
import dayjs from "dayjs";

const HarvestReport = ({ roomId }) => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoomTab, setSelectedRoomTab] = useState(0);
  const [cultivars, setCultivars] = useState([]);
  const [activeCultivarId, setActiveCultivarId] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [harvestedDate, setHarvestedDate] = useState("");
  const [plantedDate, setPlantedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  useEffect(() => {
    if (roomId) {
      setSelectedRoomTab(roomId);
      fetchGraphData(roomId);
    }
  }, [roomId]);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpService.get("rooms?sortBy=name&sortOrder=ASC");
      const roomsData = res?.data?.data || [];
      const filtered = roomsData
        .filter((room) => {
          if (!room.name) return false;
          const name = room.name.trim().toLowerCase();
          return (
            /^room\s([1-9][0-9]?)$/.test(name) ||
            ["clone room", "mom room"].includes(name)
          );
        })
        .sort((a, b) => {
          const getNumber = (name) => {
            const match = name.match(/\d+/);
            return match ? Number(match[0]) : Infinity;
          };
          return getNumber(a.name) - getNumber(b.name);
        });
      setRooms(filtered);
      if (filtered.length > 0) {
        const firstAvailableIdx = filtered.findIndex(
          (r) => r.has_harvest_batch === true,
        );
        const targetIdx = firstAvailableIdx !== -1 ? firstAvailableIdx : 0;
        setSelectedRoomTab(targetIdx);
        if (filtered[targetIdx]) {
          fetchGraphData(filtered[targetIdx].id);
        }
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGraphData = async (roomId) => {
    setChartLoading(true);
    try {
      const url = `harvest/graph-data?roomId=${roomId}`;
      const res = await httpService.get(url);
      const data = res?.data?.data || [];

      const cultivarsObj = res?.data?.cultivar || {};
      const uniqueCultivars = Object.entries(cultivarsObj).map(
        ([id, name]) => ({
          id: id,
          name: name,
        }),
      );
      setCultivars(uniqueCultivars);
      if (uniqueCultivars.length > 0) {
        setActiveCultivarId(uniqueCultivars[0].id);
      }

      const formattedData = data.map((item) => ({
        ...item,
        formatted_date: moment(item.harvest_date).format("MMM DD, YYYY"),
        wet_weight: parseFloat(item.wet_weight) || 0,
        waste: parseFloat(item.waste) || 0,
        total_wet_weight_after_waste:
          parseFloat(item.total_wet_weight_after_waste) || 0,
        bulk_lbs: parseFloat(item.bulk_lbs) || 0,
        smalls_lbs: parseFloat(item.smalls_lbs) || 0,
        trim_lbs: parseFloat(item.trim_lbs) || 0,
        total_packaged: parseFloat(item.total_packaged) || 0,
        bulk_lbs_per_plant_avg: parseFloat(item.bulk_lbs_per_plant_avg) || 0,
        smalls_lbs_per_plant_avg:
          parseFloat(item.smalls_lbs_per_plant_avg) || 0,
        trim_lbs_per_plant_avg: parseFloat(item.trim_lbs_per_plant_avg) || 0,
        total_lbs_per_plant_avg: parseFloat(item.total_lbs_per_plant_avg) || 0,
        preAvg_wet_weight: parseFloat(item.preAvg_wet_weight) || 0,
        preAvg_waste: parseFloat(item.preAvg_waste) || 0,
        preAvg_total_wet_weight_after_waste:
          parseFloat(item.preAvg_total_wet_weight_after_waste) || 0,
        preAvg_total_packaged: parseFloat(item.preAvg_total_packaged) || 0,
        preAvg_bulk_lbs: parseFloat(item.preAvg_bulk_lbs) || 0,
        preAvg_smalls_lbs: parseFloat(item.preAvg_smalls_lbs) || 0,
        preAvg_trim_lbs: parseFloat(item.preAvg_trim_lbs) || 0,
        preAvg_total_lbs_per_plant_avg:
          parseFloat(item.preAvg_total_lbs_per_plant_avg) || 0,
        preAvg_bulk_lbs_per_plant_avg:
          parseFloat(item.preAvg_bulk_lbs_per_plant_avg) || 0,
        preAvg_smalls_lbs_per_plant_avg:
          parseFloat(item.preAvg_smalls_lbs_per_plant_avg) || 0,
        preAvg_trim_lbs_per_plant_avg:
          parseFloat(item.preAvg_trim_lbs_per_plant_avg) || 0,
        preAvg_total_value_per_plant_avg:
          parseFloat(item.preAvg_total_value_per_plant_avg) || 0,
        preAvg_bulk_value_per_plant_avg:
          parseFloat(item.preAvg_bulk_value_per_plant_avg) || 0,
        preAvg_smalls_value_per_plant_avg:
          parseFloat(item.preAvg_smalls_value_per_plant_avg) || 0,
        preAvg_trim_value_per_plant_avg:
          parseFloat(item.preAvg_trim_value_per_plant_avg) || 0,
        total_value_per_plant_avg:
          parseFloat(item.total_value_per_plant_avg) || 0,
        bulk_value_per_plant_avg:
          parseFloat(item.bulk_value_per_plant_avg) || 0,
        smalls_value_per_plant_avg:
          parseFloat(item.smalls_value_per_plant_avg) || 0,
        trim_value_per_plant_avg:
          parseFloat(item.trim_value_per_plant_avg) || 0,
      }));
      setGraphData(formattedData);
      if (formattedData.length > 0) {
        const lastRecord = formattedData[formattedData.length - 1];
        setSelectedDate(lastRecord.formatted_date);
        setHarvestedDate(lastRecord.harvest_date);
      } else {
        setSelectedDate("");
        setHarvestedDate("");
        setPlantedDate("");
      }
    } catch (error) {
      console.error("Error fetching graph data:", error);
      setGraphData([]);
      setSelectedDate("");
    } finally {
      setChartLoading(false);
    }
  };

  const chartDatasets = useMemo(() => {
    if (!selectedDate || graphData.length === 0)
      return { weights: [], avgWeights: [], avgValues: [] };
    const record = graphData.find(
      (d) =>
        d.formatted_date === selectedDate &&
        (!activeCultivarId ||
          String(d.cultivar_id) === String(activeCultivarId)),
    );
    if (!record) return { weights: [], avgWeights: [], avgValues: [] };

    const wetWeight = [
      {
        category: "Wet Weight",
        current: record.wet_weight,
        average: record.preAvg_wet_weight,
      },
      {
        category: "Waste",
        current: record.waste,
        average: record.preAvg_waste,
      },
      {
        category: "Total Wet Weight After Waste",
        current: record.total_wet_weight_after_waste,
        average: record.preAvg_total_wet_weight_after_waste,
      },
    ];

    const weights = [
      {
        category: "Bulk",
        current: record.bulk_lbs,
        average: record.preAvg_bulk_lbs,
      },
      {
        category: "Smalls",
        current: record.smalls_lbs,
        average: record.preAvg_smalls_lbs,
      },
      {
        category: "Trim",
        current: record.trim_lbs,
        average: record.preAvg_trim_lbs,
      },
      {
        category: "Total",
        current: record.total_packaged,
        average: record.preAvg_total_packaged,
      },
    ];

    const avgWeights = [
      {
        category: "Bulk",
        current: record.bulk_lbs_per_plant_avg,
        average: record.preAvg_bulk_lbs_per_plant_avg,
      },
      {
        category: "Smalls",
        current: record.smalls_lbs_per_plant_avg,
        average: record.preAvg_smalls_lbs_per_plant_avg,
      },
      {
        category: "Trim",
        current: record.trim_lbs_per_plant_avg,
        average: record.preAvg_trim_lbs_per_plant_avg,
      },
      {
        category: "Total",
        current: record.total_lbs_per_plant_avg,
        average: record.preAvg_total_lbs_per_plant_avg,
      },
    ];

    const avgValues = [
      {
        category: "Bulk",
        current: record.bulk_value_per_plant_avg,
        average: record.preAvg_bulk_value_per_plant_avg,
      },
      {
        category: "Smalls",
        current: record.smalls_value_per_plant_avg,
        average: record.preAvg_smalls_value_per_plant_avg,
      },
      {
        category: "Trim",
        current: record.trim_value_per_plant_avg,
        average: record.preAvg_trim_value_per_plant_avg,
      },
      {
        category: "Total",
        current: record.total_value_per_plant_avg,
        average: record.preAvg_total_value_per_plant_avg,
      },
    ];

    return { weights, avgWeights, avgValues, wetWeight };
  }, [selectedDate, graphData, activeCultivarId]);

  useEffect(() => {
    if (roomId == null) fetchRooms();
  }, [fetchRooms]);

  const handleRoomTabChange = (event, newValue) => {
    setSelectedRoomTab(newValue);
    const roomId = rooms[newValue]?.id;
    if (roomId) {
      fetchGraphData(roomId);
    }
  };

  const handleCultivarChange = (cultivarId) => {
    setActiveCultivarId(cultivarId);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p className="label" style={{ fontWeight: "bold" }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: 0 }}>
              {`${entry.name}: ${typeof entry.value === "number" ? entry.value.toFixed(4) : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="harvest-report coa-report-archive">
      {loading && <FullScreenLoader />}

      {roomId == null && (
        <div className="page-title mb-4">
          <div className="title-wrap d-flex justify-content-between align-items-center w-100">
            <h1>Harvest Report</h1>
          </div>
        </div>
      )}

      {roomId == null && (
        <Tabs
          value={selectedRoomTab}
          onChange={handleRoomTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            backgroundColor: "#fffffc",
            "& .MuiTabs-indicator": {
              backgroundColor: "#00A272",
              height: "3px",
            },
            "& .MuiTab-root": {
              position: "relative",
              color: "#A1A2A1",
              fontWeight: 400,
              fontSize: "16px",
              textTransform: "none",
              padding: "12px 20px",
              marginRight: "8px",
              "&:not(.Mui-selected)::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "3px",
                backgroundColor: "#C4C4C4",
              },
              "&.Mui-selected": {
                color: "#00A272 !important",
                fontWeight: 600,
                backgroundColor: "#E6F6F1",
                borderRadius: "4px 4px 0 0",
              },
            },
            mb: 3,
          }}
        >
          {rooms.map((room) => (
            <Tab
              key={room.id}
              label={room.name}
              disabled={!room?.has_harvest_batch}
              sx={{
                "&.Mui-disabled": {
                  color: "#A1A2A1",
                  opacity: 0.5,
                },
              }}
            />
          ))}
        </Tabs>
      )}

      <Paper className="report-paper" elevation={0}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 4,
            mb: 3,
            backgroundColor: "#fff",
            p: 2,
            borderRadius: "12px",
            border: "1px solid #e8edf2",
            justifyContent: "space-between",
          }}
        >
          <div className="form_field_wrapper d-flex align-items-center m-0 pe-4">
            {/* <label>Cultivars : </label> */}
            <div className="d-flex flex-wrap gap-4">
              {cultivars.map((cultivar) => {
                const isActive =
                  String(activeCultivarId) === String(cultivar.id);
                return (
                  <div
                    key={cultivar.id}
                    style={{
                      color: isActive ? "#681da8" : "#007bff",
                      textDecoration: "underline",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                    onClick={() => handleCultivarChange(cultivar.id)}
                  >
                    {cultivar.name}
                  </div>
                );
              })}
            </div>
          </div>

          {/* <div className="form_field_wrapper d-flex align-items-center m-0 pe-4 border-end">
            <label>Planted : </label>
            <span>
              {plantedDate ? dayjs(plantedDate).format("MM-DD-YYYY") : "—"}
            </span>
          </div> */}

          <div className="form_field_wrapper d-flex align-items-center m-0">
            <label>Harvested : </label>
            <span>
              {harvestedDate ? dayjs(harvestedDate).format("MM-DD-YYYY") : "—"}
            </span>
          </div>
        </Box>

        <div className="rlr-charts-grid">
          {chartLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}

          {[
            {
              title: "Wet Weight (lbs)",
              data: chartDatasets.wetWeight,
              color: "#85C1E9",
            },
            {
              title: "Total Weights (lbs)",
              data: chartDatasets.weights,
              color: "#85C1E9",
            },
            {
              title: "Average Weights (lbs/plant)",
              data: chartDatasets.avgWeights,
              color: "#85C1E9",
            },
            {
              title: "Average Value ($/plant)",
              data: chartDatasets.avgValues,
              color: "#85C1E9",
            },
          ]?.map((chart, idx) => (
            <div key={idx} className="rlr-chart-card">
              <h3 className="rlr-chart-title">{chart?.title}</h3>
              {chart?.data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chart?.data}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#E8EDF2"
                    />
                    <XAxis
                      dataKey="category"
                      tick={{ fontSize: 12, fontWeight: 600 }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      iconType="rect"
                      wrapperStyle={{ fontSize: "12px" }}
                    />
                    <Bar
                      dataKey="current"
                      name="Current Parameter"
                      fill={chart.color}
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList
                        dataKey="current"
                        position="top"
                        formatter={(val) => val.toFixed(2)}
                        style={{ fontSize: 10, fontWeight: 600 }}
                      />
                    </Bar>
                    <Bar
                      dataKey="average"
                      name="Cultivar Average"
                      fill="#D35400"
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList
                        dataKey="average"
                        position="top"
                        formatter={(val) => val.toFixed(2)}
                        style={{ fontSize: 10, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="rlr-no-data">No data available</div>
              )}
            </div>
          ))}
        </div>
      </Paper>
    </div>
  );
};

export default HarvestReport;
